/**
 * Badge Definitions
 *
 * System odznak dla użytkowników aplikacji.
 * Odznaki mogą być przyznawane na podstawie:
 * - user_role (rola w systemie)
 * - aktywności (liczba wydarzeń, znajomych, itd.)
 *
 * Ikony: komponent BadgeGraphic (badge.id), nie pole `icon`.
 */

// Odznaki oparte na roli użytkownika
export const ROLE_BADGES = {
  user: {
    id: 'role_user',
    label: 'Użytkownik',
    labelKey: 'badges.roles.user',
    descriptionKey: 'badges.roles.userDescription',
    color: '#78716c',
    bgColor: '#f5f5f4',
    borderColor: '#e7e5e4',
    priority: 1,
    rarity: 'common',
  },
  organizer: {
    id: 'role_organizer',
    label: 'Organizator',
    labelKey: 'badges.roles.organizer',
    descriptionKey: 'badges.roles.organizerDescription',
    color: '#E55A32',
    bgColor: '#fff7ed',
    borderColor: '#fed7aa',
    priority: 2,
    rarity: 'rare',
  },
  moderator: {
    id: 'role_moderator',
    label: 'Moderator',
    labelKey: 'badges.roles.moderator',
    descriptionKey: 'badges.roles.moderatorDescription',
    color: '#c2410c',
    bgColor: '#fff7ed',
    borderColor: '#fed7aa',
    priority: 3,
    rarity: 'epic',
  },
  admin: {
    id: 'role_admin',
    label: 'Administrator',
    labelKey: 'badges.roles.admin',
    descriptionKey: 'badges.roles.adminDescription',
    color: '#7c2d12',
    bgColor: '#fef3c7',
    borderColor: '#fcd34d',
    priority: 4,
    rarity: 'legendary',
  },
};

// Odznaki oparte na aktywności (do rozbudowy w przyszłości)
export const ACTIVITY_BADGES = {
  // Uczestnictwo w wydarzeniach
  starter: {
    id: 'activity_starter',
    label: 'Początkujący',
    labelKey: 'badges.activity.starter',
    descriptionKey: 'badges.activity.starterDescription',
    color: '#15803d',
    bgColor: '#dcfce7',
    borderColor: '#86efac',
    rarity: 'common',
    sortOrder: 10,
    condition: (stats) => stats.completedEvents >= 1 && stats.completedEvents < 5,
  },
  regular: {
    id: 'activity_regular',
    label: 'Stały bywalec',
    labelKey: 'badges.activity.regular',
    descriptionKey: 'badges.activity.regularDescription',
    color: '#ca8a04',
    bgColor: '#fef9c3',
    borderColor: '#fde047',
    rarity: 'uncommon',
    sortOrder: 20,
    condition: (stats) => stats.completedEvents >= 5 && stats.completedEvents < 10,
  },
  veteran: {
    id: 'activity_veteran',
    label: 'Weteran',
    labelKey: 'badges.activity.veteran',
    descriptionKey: 'badges.activity.veteranDescription',
    color: '#b45309',
    bgColor: '#fef3c7',
    borderColor: '#fcd34d',
    rarity: 'rare',
    sortOrder: 30,
    condition: (stats) => stats.completedEvents >= 10,
  },

  // Społeczność
  social: {
    id: 'activity_social',
    label: 'Towarzyski',
    labelKey: 'badges.activity.social',
    descriptionKey: 'badges.activity.socialDescription',
    color: '#0891b2',
    bgColor: '#ecfeff',
    borderColor: '#a5f3fc',
    rarity: 'uncommon',
    sortOrder: 40,
    condition: (stats) => stats.friendsCount >= 5,
  },
  butterfly: {
    id: 'activity_butterfly',
    label: 'Dusza towarzystwa',
    labelKey: 'badges.activity.butterfly',
    descriptionKey: 'badges.activity.butterflyDescription',
    color: '#7c3aed',
    bgColor: '#f5f3ff',
    borderColor: '#c4b5fd',
    rarity: 'rare',
    sortOrder: 50,
    condition: (stats) => stats.friendsCount >= 10,
  },

  // Organizowanie
  host: {
    id: 'activity_host',
    label: 'Organizator wydarzeń',
    labelKey: 'badges.activity.host',
    descriptionKey: 'badges.activity.hostDescription',
    color: '#dc2626',
    bgColor: '#fef2f2',
    borderColor: '#fecaca',
    rarity: 'uncommon',
    sortOrder: 60,
    condition: (stats) => stats.organizedEvents >= 1,
  },
  hostPro: {
    id: 'activity_host_pro',
    label: 'Pro organizator',
    labelKey: 'badges.activity.hostPro',
    descriptionKey: 'badges.activity.hostProDescription',
    color: '#9333ea',
    bgColor: '#faf5ff',
    borderColor: '#d8b4fe',
    rarity: 'epic',
    sortOrder: 70,
    condition: (stats) => stats.organizedEvents >= 5,
  },

  // Odkrywca
  explorer: {
    id: 'activity_explorer',
    label: 'Odkrywca',
    labelKey: 'badges.activity.explorer',
    descriptionKey: 'badges.activity.explorerDescription',
    color: '#0d9488',
    bgColor: '#f0fdfa',
    borderColor: '#99f6e4',
    rarity: 'rare',
    sortOrder: 80,
    condition: (stats) => stats.uniqueCities >= 3,
  },

  // Wyróżnienia społeczności (flagi w profilu / users — patrz profileStatsService)
  betaTester: {
    id: 'activity_beta_tester',
    label: 'Beta tester',
    labelKey: 'badges.activity.betaTester',
    descriptionKey: 'badges.activity.betaTesterDescription',
    color: '#4338ca',
    bgColor: '#eef2ff',
    borderColor: '#a5b4fc',
    rarity: 'epic',
    sortOrder: 90,
    condition: (stats) => Boolean(stats?.isBetaTester),
  },
  earlyBeliever: {
    id: 'activity_early_believer',
    label: 'Wczesny abonament',
    labelKey: 'badges.activity.earlyBeliever',
    descriptionKey: 'badges.activity.earlyBelieverDescription',
    color: '#b45309',
    bgColor: '#fffbeb',
    borderColor: '#fcd34d',
    rarity: 'legendary',
    sortOrder: 100,
    condition: (stats) => Boolean(stats?.preLaunchSubscriber),
  },
  supporterDonor: {
    id: 'activity_supporter_donor',
    label: 'Wspierający',
    labelKey: 'badges.activity.supporterDonor',
    descriptionKey: 'badges.activity.supporterDonorDescription',
    color: '#be185d',
    bgColor: '#fdf2f8',
    borderColor: '#f9a8d4',
    rarity: 'epic',
    sortOrder: 110,
    condition: (stats) => Boolean(stats?.supporterDonor),
  },
};

/**
 * Pobierz odznakę roli użytkownika
 * @param {string} userRole - Rola w systemie (user, organizator, moderator, admin, …)
 * @returns {object|null} - Obiekt odznaki lub null
 */
export function getRoleBadge(userRole) {
  if (!userRole || userRole === 'user') {
    return null;
  }
  if (userRole === 'organizator') {
    return ROLE_BADGES.organizer;
  }
  return ROLE_BADGES[userRole] || null;
}

/**
 * Pobierz odznaki aktywności użytkownika
 * @param {object} stats - Statystyki użytkownika
 * @param {number} stats.completedEvents - Liczba ukończonych wydarzeń
 * @param {number} stats.organizedEvents - Liczba zorganizowanych wydarzeń
 * @param {number} stats.friendsCount - Liczba znajomych
 * @param {number} stats.uniqueCities - Liczba unikalnych miast
 * @param {boolean} [stats.isBetaTester] - Uczestnik testów beta
 * @param {boolean} [stats.preLaunchSubscriber] - Abonament przed premierą
 * @param {boolean} [stats.supporterDonor] - Wsparcie finansowe projektu
 * @returns {array} - Tablica odznak aktywności
 */
export function getActivityBadges(stats) {
  if (!stats) return [];

  const badges = [];

  Object.values(ACTIVITY_BADGES).forEach((badge) => {
    if (badge.condition && badge.condition(stats)) {
      badges.push(badge);
    }
  });

  return badges;
}

/**
 * Pobierz wszystkie odznaki użytkownika
 * @param {string} userRole - Rola użytkownika
 * @param {object} stats - Statystyki użytkownika
 * @param {object} options - Opcje
 * @param {boolean} options.includeRoleBadge - Czy dołączyć odznakę roli (domyślnie true)
 * @param {boolean} options.includeActivityBadges - Czy dołączyć odznaki aktywności (domyślnie true)
 * @returns {array} - Tablica wszystkich odznak
 */
const ROLE_BADGE_KEYS = ['user', 'organizer', 'moderator', 'admin'];

/** Odznaki ról user / moderator / admin — bez publicznego udziału populacji w UI (dymek, itp.). */
const BADGE_IDS_HIDE_PUBLIC_UNLOCK_STATS = new Set([
  'role_user',
  'role_moderator',
  'role_admin',
]);

/**
 * Czy dla danej odznaki nie pokazujemy statystyk „ile % osób zdobyło”.
 */
export function badgeHidesPublicUnlockStats(badgeId) {
  return typeof badgeId === 'string' && BADGE_IDS_HIDE_PUBLIC_UNLOCK_STATS.has(badgeId);
}

/**
 * Czy odznaka roli jest zdobyta dla danej roli konta (API: user, organizator, moderator, admin).
 */
export function isRoleBadgeUnlocked(roleKey, userRole) {
  const r = userRole || 'user';
  if (roleKey === 'user') return r === 'user';
  if (roleKey === 'organizer') return r === 'organizator';
  if (roleKey === 'moderator') return r === 'moderator';
  if (roleKey === 'admin') return r === 'admin';
  return false;
}

/**
 * Pełny katalog odznak ról z flagą unlocked (np. do popupu osiągnięć).
 */
export function getRoleAchievementsCatalog(userRole) {
  return ROLE_BADGE_KEYS.map((roleKey) => {
    const badge = ROLE_BADGES[roleKey];
    return {
      kind: 'role',
      roleKey,
      ...badge,
      unlocked: isRoleBadgeUnlocked(roleKey, userRole),
    };
  }).sort((a, b) => (a.priority || 0) - (b.priority || 0));
}

/**
 * Pełny katalog odznak aktywności z flagą unlocked.
 */
export function getActivityAchievementsCatalog(stats) {
  const s = stats || {};
  return Object.values(ACTIVITY_BADGES)
    .map((badge) => ({
      kind: 'activity',
      ...badge,
      unlocked: Boolean(badge.condition && badge.condition(s)),
    }))
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
}

export function getAllBadges(userRole, stats, options = {}) {
  const {
    includeRoleBadge = true,
    includeActivityBadges = true,
  } = options;

  const badges = [];

  // Dodaj odznakę roli (jeśli nie jest zwykłym userem lub opcja wymusza)
  if (includeRoleBadge && userRole && userRole !== 'user') {
    const roleBadge = getRoleBadge(userRole);
    if (roleBadge) {
      badges.push(roleBadge);
    }
  }

  // Dodaj odznaki aktywności
  if (includeActivityBadges && stats) {
    const activityBadges = getActivityBadges(stats);
    badges.push(...activityBadges);
  }

  // Najpierw odznaka roli, potem reszta: priorytet, potem sortOrder (katalog aktywności).
  return badges.sort((a, b) => {
    const aRole = typeof a.id === 'string' && a.id.startsWith('role_');
    const bRole = typeof b.id === 'string' && b.id.startsWith('role_');
    if (aRole !== bRole) return (bRole ? 1 : 0) - (aRole ? 1 : 0);
    const pd = (b.priority || 0) - (a.priority || 0);
    if (pd !== 0) return pd;
    return (a.sortOrder || 0) - (b.sortOrder || 0);
  });
}
